import React, { useState, useEffect } from "react";
import { X, Cookie, Settings } from "lucide-react";

const CookieBanner = ({ onAccept, onDecline, onUpdatePreferences }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [showPreferences, setShowPreferences] = useState(false);
	const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

	useEffect(() => {
		// Check if user has already made a choice
		const cookieConsent = localStorage.getItem('cookie-consent');
		if (!cookieConsent) {
			// Show banner after a short delay
			const timer = setTimeout(() => {
				setIsVisible(true);
			}, 1000);
			return () => clearTimeout(timer);
		} else {
			// Load existing preferences
			const preferences = JSON.parse(cookieConsent);
			setAnalyticsEnabled(preferences.analytics || false);
		}
	}, []);

	const handleAcceptAll = () => {
		const preferences = { essential: true, analytics: true };
		setIsClosing(true);
		localStorage.setItem('cookie-consent', JSON.stringify(preferences));
		setTimeout(() => {
			setIsVisible(false);
			onAccept && onAccept(preferences);
		}, 300);
	};

	const handleDeclineAll = () => {
		const preferences = { essential: true, analytics: false };
		setIsClosing(true);
		localStorage.setItem('cookie-consent', JSON.stringify(preferences));
		setTimeout(() => {
			setIsVisible(false);
			onDecline && onDecline(preferences);
		}, 300);
	};

	const handleSavePreferences = () => {
		const preferences = { essential: true, analytics: analyticsEnabled };
		setIsClosing(true);
		localStorage.setItem('cookie-consent', JSON.stringify(preferences));
		setTimeout(() => {
			setIsVisible(false);
			onUpdatePreferences && onUpdatePreferences(preferences);
		}, 300);
	};

	const handleClose = () => {
		setIsClosing(true);
		setTimeout(() => {
			setIsVisible(false);
		}, 300);
	};

	if (!isVisible) return null;

	return (
		<div className={`fixed bottom-4 left-4 right-4 sm:left-6 sm:right-6 z-50 transition-all duration-300 ${
			isClosing ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'
		}`}>
			<div className="bg-white border border-slate-200 rounded-lg shadow-lg max-w-md mx-auto sm:mx-0 sm:max-w-lg">
				<div className="p-4 sm:p-5">
					{!showPreferences ? (
						// Main banner view
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0 mt-0.5">
								<Cookie className="w-5 h-5 text-slate-600" />
							</div>
							<div className="flex-1 min-w-0">
								<h3 className="text-sm font-semibold text-slate-900 mb-2">
									We use cookies
								</h3>
								<p className="text-sm text-slate-600 mb-4 leading-relaxed">
									We use Google Analytics to understand how visitors interact with our website. 
									Essential cookies are always enabled for basic functionality.
								</p>
								<div className="flex flex-col sm:flex-row gap-2">
									<button
										onClick={handleAcceptAll}
										className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
									>
										Accept All
									</button>
									<button
										onClick={handleDeclineAll}
										className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 transition-colors"
									>
										Decline Analytics
									</button>
									<button
										onClick={() => setShowPreferences(true)}
										className="px-3 py-2 text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors flex items-center gap-1"
									>
										<Settings className="w-3 h-3" />
										Customise
									</button>
								</div>
							</div>
							<button
								onClick={handleClose}
								className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 transition-colors"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
					) : (
						// Preferences view
						<div>
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-sm font-semibold text-slate-900">
									Cookie Preferences
								</h3>
								<button
									onClick={handleClose}
									className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
								>
									<X className="w-4 h-4" />
								</button>
							</div>
							
							<div className="space-y-4 mb-4">
								{/* Essential cookies */}
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<h4 className="text-sm font-medium text-slate-900">Essential</h4>
										<p className="text-xs text-slate-600 mt-1">
											Required for basic website functionality. Cannot be disabled.
										</p>
									</div>
									<div className="ml-3 mt-0.5">
										<div className="w-10 h-5 bg-slate-900 rounded-full flex items-center">
											<div className="w-4 h-4 bg-white rounded-full ml-auto mr-0.5"></div>
										</div>
									</div>
								</div>

								{/* Analytics cookies */}
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<h4 className="text-sm font-medium text-slate-900">Analytics</h4>
										<p className="text-xs text-slate-600 mt-1">
											Google Analytics to help us understand website usage and improve your experience.
										</p>
									</div>
									<div className="ml-3 mt-0.5">
										<button
											onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
											className={`w-10 h-5 rounded-full transition-colors ${
												analyticsEnabled ? 'bg-slate-900' : 'bg-slate-300'
											}`}
										>
											<div className={`w-4 h-4 bg-white rounded-full transition-transform ${
												analyticsEnabled ? 'ml-auto mr-0.5' : 'ml-0.5'
											}`}></div>
										</button>
									</div>
								</div>
							</div>

							<div className="flex gap-2">
								<button
									onClick={() => setShowPreferences(false)}
									className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 transition-colors"
								>
									Back
								</button>
								<button
									onClick={handleSavePreferences}
									className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
								>
									Save Preferences
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// Cookie Preferences Management Component (for footer/settings page)
const CookiePreferencesManager = ({ onUpdatePreferences }) => {
	const [preferences, setPreferences] = useState({ essential: true, analytics: false });
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		const saved = localStorage.getItem('cookie-consent');
		if (saved) {
			setPreferences(JSON.parse(saved));
		}
	}, []);

	const handleSave = () => {
		localStorage.setItem('cookie-consent', JSON.stringify(preferences));
		setShowModal(false);
		onUpdatePreferences && onUpdatePreferences(preferences);
	};

	return (
		<>
			<span
				onClick={() => setShowModal(true)}
				href=""
				className="text-slate-600 hover:text-slate-900 transition-colors text-sm cursor-pointer"
			>
				Cookie Preferences
			</span>

			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
					<div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowModal(false)} />
					<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-lg font-semibold text-slate-900">Cookie Preferences</h2>
							<button
								onClick={() => setShowModal(false)}
								className="p-1 text-slate-400 hover:text-slate-600"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<div className="space-y-4 mb-6">
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<h3 className="text-sm font-medium text-slate-900">Essential</h3>
									<p className="text-xs text-slate-600 mt-1">
										Required for basic website functionality. Cannot be disabled.
									</p>
								</div>
								<div className="ml-3 mt-0.5">
									<div className="w-10 h-5 bg-slate-900 rounded-full flex items-center">
										<div className="w-4 h-4 bg-white rounded-full ml-auto mr-0.5"></div>
									</div>
								</div>
							</div>

							<div className="flex items-start justify-between">
								<div className="flex-1">
									<h3 className="text-sm font-medium text-slate-900">Analytics</h3>
									<p className="text-xs text-slate-600 mt-1">
										Google Analytics to help us understand website usage.
									</p>
								</div>
								<div className="ml-3 mt-0.5">
									<button
										onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
										className={`w-10 h-5 rounded-full transition-colors ${
											preferences.analytics ? 'bg-slate-900' : 'bg-slate-300'
										}`}
									>
										<div className={`w-4 h-4 bg-white rounded-full transition-transform ${
											preferences.analytics ? 'ml-auto mr-0.5' : 'ml-0.5'
										}`}></div>
									</button>
								</div>
							</div>
						</div>

						<div className="flex gap-3">
							<button
								onClick={() => setShowModal(false)}
								className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleSave}
								className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export { CookieBanner, CookiePreferencesManager };
export default CookieBanner;